import React, { useState } from "react";
import { FROSTED_PANEL_STYLE } from "./constants";

function AssignModal({ metric, workspaces, onClose, onAssign, initiallyAssignedIds = [] }) {
  // Workspaces the metric is already on when the modal opens. Shown checked
  // and locked (can't be unchecked here — removing an assignment isn't
  // supported by this modal) so they're clearly "already there", and so
  // Assign only ever creates copies for genuinely new selections below —
  // never re-submits an already-assigned workspace as if it were new.
  const alreadyAssignedIds = new Set(Array.isArray(initiallyAssignedIds) ? initiallyAssignedIds : []);
  const [selectedIds, setSelectedIds] = useState(() => new Set(alreadyAssignedIds));
  const [search, setSearch] = useState("");
  const [isAssigning, setIsAssigning] = useState(false);

  const filtered = workspaces.filter((ws) =>
    ws.name.toLowerCase().includes(search.toLowerCase())
  );

  const selectWorkspace = (id) => {
    if (alreadyAssignedIds.has(id)) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/15 backdrop-blur-[3px]" onClick={() => !isAssigning && onClose()} />
      <div className="rounded-lg w-full max-w-[440px] relative z-10 flex flex-col overflow-hidden max-h-[85vh]" style={FROSTED_PANEL_STYLE}>
        {/* Header — icon + title + subtitle, X close top-right */}
        <header className="flex items-start justify-between px-[20px] py-[16px] border-b border-[rgba(15,23,42,0.06)] flex-shrink-0">
          <div className="flex items-start gap-[11px] min-w-0">
            <div className="w-[34px] h-[34px] rounded-lg flex-shrink-0 flex items-center justify-center bg-[var(--gmb-primary-soft)] text-[var(--gmb-primary)]">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-[17px] h-[17px]">
                <path d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <div className="min-w-0">
              <h3 className="text-[15px] font-semibold text-gray-900 leading-[1.25]">Assign metric</h3>
              <p className="text-[12.5px] text-gray-500 mt-[3px] leading-[1.4]">
                Choose one or more workspaces to assign <span className="text-gray-900 font-semibold">&quot;{metric.name}&quot;</span> to. The same metric will show on every workspace picked — editing it is only possible from Company Metrics.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => !isAssigning && onClose()}
            aria-label="Close"
            className="w-[30px] h-[30px] rounded-[7px] flex-shrink-0 flex items-center justify-center bg-[rgba(15,23,42,0.04)] text-gray-400 hover:bg-[rgba(15,23,42,0.07)] hover:text-gray-600 transition-colors"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[15px] h-[15px]" aria-hidden>
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div className="px-[20px] py-[16px] overflow-y-auto">
          <div className="relative mb-[12px]">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="absolute left-[12px] top-1/2 -translate-y-1/2 w-[15px] h-[15px] text-gray-400">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search workspaces and sub workspaces..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-[34px] pr-[12px] py-[9px] bg-[#f8fafc] border border-[#eef0f2] rounded-[9px] text-[13px] focus:border-[var(--gmb-primary)] focus:bg-white outline-none transition-all"
            />
          </div>

          <div className="flex flex-col gap-[7px] max-h-[280px] overflow-y-auto pr-[4px] custom-scrollbar">
            {filtered.map((ws) => {
              const isSelected = selectedIds.has(ws.id);
              const isLocked = alreadyAssignedIds.has(ws.id);
              return (
                <label
                  key={ws.id}
                  className={`flex items-center gap-[12px] p-[10px] px-[12px] rounded-[10px] border transition-all ${
                    isLocked ? "cursor-default" : "cursor-pointer"
                  } ${
                    isSelected ? "border-[var(--gmb-primary)] bg-[var(--gmb-primary-soft)]" : "border-[#eef0f2] bg-white hover:border-[#dde2e8]"
                  }`}
                >
                  <div className="w-[32px] h-[32px] rounded-lg overflow-hidden flex-shrink-0 border border-[#eef0f2] shadow-sm bg-gray-100 flex items-center justify-center">
                    {ws.icon ? <img src={ws.icon} alt={ws.name} className="w-full h-full object-cover" /> : <span className="text-gray-400 text-xs font-semibold">W</span>}
                  </div>
                  <span className={`flex-grow text-[13.5px] font-medium ${isSelected ? "text-[var(--gmb-primary)]" : "text-gray-700"}`}>
                    {ws.name}
                  </span>
                  {isLocked && (
                    <span className="text-[11px] font-medium text-gray-400 flex-shrink-0">Already assigned</span>
                  )}
                  <div className="relative flex items-center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isLocked}
                      onChange={() => selectWorkspace(ws.id)}
                      className="sr-only"
                    />
                    <div
                      className={`w-[18px] h-[18px] rounded-[5px] border-2 flex items-center justify-center transition-all ${
                        isSelected ? "bg-[var(--gmb-primary)] border-[var(--gmb-primary)]" : "bg-white border-gray-300"
                      } ${isLocked ? "opacity-60" : ""}`}
                    >
                      {isSelected && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="w-[11px] h-[11px] text-white">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      )}
                    </div>
                  </div>
                </label>
              );
            })}
            {filtered.length === 0 && (
              <div className="py-[40px] text-center flex flex-col items-center">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-[26px] h-[26px] mb-[8px] text-gray-300">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <div className="text-gray-400 text-[13px] font-medium">No workspaces or sub workspaces found matching your search.</div>
              </div>
            )}
          </div>
        </div>

        {/* Footer — Cancel + Assign, grouped bottom-right */}
        <footer className="flex items-center justify-end gap-[10px] px-[20px] py-[14px] border-t border-[rgba(15,23,42,0.06)] bg-[rgba(248,250,252,0.5)] flex-shrink-0">
          <button
            onClick={onClose}
            disabled={isAssigning}
            className="px-[24px] py-[8px] rounded-lg font-medium text-[13px] text-slate-600 border border-[rgba(15,23,42,0.1)] hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:pointer-events-none"
          >
            Cancel
          </button>
          <button
            onClick={async () => {
              // Only ever submit workspaces newly picked in this session —
              // already-assigned ones are locked/uncheckable above, but this
              // filter is the real guarantee against ever re-submitting one.
              const newIds = Array.from(selectedIds).filter((id) => !alreadyAssignedIds.has(id));
              if (!newIds.length || isAssigning) return;
              setIsAssigning(true);
              try { await onAssign(metric, newIds); } finally { setIsAssigning(false); }
            }}
            disabled={selectedIds.size <= alreadyAssignedIds.size || isAssigning}
            className="px-[24px] py-[8px] bg-[var(--gmb-primary)] text-white rounded-lg font-medium text-[13px] shadow-sm hover:brightness-95 transition-all disabled:opacity-40 disabled:pointer-events-none flex items-center gap-[8px]"
          >
            {isAssigning && (
              <svg className="animate-spin w-[15px] h-[15px] text-white" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {(() => {
              if (isAssigning) return "Assigning...";
              const newCount = selectedIds.size - alreadyAssignedIds.size;
              if (newCount > 1) return `Assign to ${newCount} workspaces`;
              return "Assign";
            })()}
          </button>
        </footer>
      </div>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #E5E7EB; border-radius: 10px; }
      `}</style>
    </div>
  );
}

export default AssignModal;
